import { useState, useCallback } from 'react';
import EditorToolbar from './components/EditorToolbar';
import EditorLeftPanel from './components/EditorLeftPanel';
import EditorCanvas from './components/EditorCanvas';
import EditorRightPanel from './components/EditorRightPanel';
import AIModal from './components/AIModal';
import PreviewModal from './components/PreviewModal';
import { mockEditorPages } from '@/mocks/editorLayout';
import { mockArticles } from '@/mocks/articles';
import { mockAds } from '@/mocks/editorLayout';
import type { LayoutBlock, EditorPage, BlockType } from '@/mocks/editorLayout';

let blockIdCounter = 100;
const genId = () => `b${++blockIdCounter}`;

let pageIdCounter = 10;
const genPageId = () => `page-${++pageIdCounter}`;

export default function EditorPage() {
  const [pages, setPages] = useState<EditorPage[]>(mockEditorPages);
  const [currentPageId, setCurrentPageId] = useState(mockEditorPages[0].id);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(75);
  const [aiAction, setAiAction] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showExportToast, setShowExportToast] = useState(false);
  const [showNewArticleModal, setShowNewArticleModal] = useState(false);
  const [history, setHistory] = useState<EditorPage[][]>([mockEditorPages]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [pageAddedToast, setPageAddedToast] = useState<string | null>(null);

  const currentPage = pages.find((p) => p.id === currentPageId) || pages[0];
  const selectedBlock = currentPage.blocks.find((b) => b.id === selectedBlockId) || null;

  const pushHistory = useCallback((newPages: EditorPage[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newPages);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const updatePages = useCallback((newPages: EditorPage[]) => {
    setPages(newPages);
    pushHistory(newPages);
  }, [pushHistory]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prev = history[historyIndex - 1];
      setPages(prev);
      setHistoryIndex(historyIndex - 1);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const next = history[historyIndex + 1];
      setPages(next);
      setHistoryIndex(historyIndex + 1);
    }
  };

  // ── PAGE MANAGEMENT ──────────────────────────────────────────────────────────

  const handleReorderPages = useCallback((newPages: EditorPage[]) => {
    updatePages(newPages);
  }, [updatePages]);

  const handleAddPage = useCallback((section: string, type: EditorPage['type']) => {
    const newPage: EditorPage = {
      id: genPageId(),
      number: pages.length + 1,
      type,
      section,
      blocks: [],
      status: 'rascunho',
    };
    const newPages = [...pages, newPage];
    updatePages(newPages);
    setCurrentPageId(newPage.id);
    setSelectedBlockId(null);
    setPageAddedToast(section);
    setTimeout(() => setPageAddedToast(null), 2500);
  }, [pages, updatePages]);

  const handleDeletePage = useCallback((pageId: string) => {
    if (pages.length <= 1) return;
    const newPages = pages
      .filter((p) => p.id !== pageId)
      .map((p, i) => ({ ...p, number: i + 1 }));
    if (currentPageId === pageId) {
      setCurrentPageId(newPages[0].id);
    }
    setSelectedBlockId(null);
    updatePages(newPages);
  }, [pages, currentPageId, updatePages]);

  const handleDuplicatePage = useCallback((pageId: string) => {
    const original = pages.find((p) => p.id === pageId);
    if (!original) return;
    const insertIndex = pages.findIndex((p) => p.id === pageId) + 1;
    const duplicated: EditorPage = {
      ...original,
      id: genPageId(),
      status: 'rascunho',
      blocks: original.blocks.map((b) => ({ ...b, id: genId() })),
    };
    const newPages = [
      ...pages.slice(0, insertIndex),
      duplicated,
      ...pages.slice(insertIndex),
    ].map((p, i) => ({ ...p, number: i + 1 }));
    updatePages(newPages);
    setCurrentPageId(duplicated.id);
    setSelectedBlockId(null);
  }, [pages, updatePages]);

  const handleChangePageStatus = useCallback((pageId: string, status: EditorPage['status']) => {
    const newPages = pages.map((p) => p.id === pageId ? { ...p, status } : p);
    updatePages(newPages);
  }, [pages, updatePages]);

  // ── BLOCK MANAGEMENT ─────────────────────────────────────────────────────────

  const updateBlock = useCallback((blockId: string, updates: Partial<LayoutBlock>) => {
    const newPages = pages.map((p) =>
      p.id === currentPageId
        ? { ...p, blocks: p.blocks.map((b) => b.id === blockId ? { ...b, ...updates } : b) }
        : p
    );
    setPages(newPages);
  }, [pages, currentPageId]);

  const handleUpdateBlockWithHistory = useCallback((blockId: string, updates: Partial<LayoutBlock>) => {
    const newPages = pages.map((p) =>
      p.id === currentPageId
        ? { ...p, blocks: p.blocks.map((b) => b.id === blockId ? { ...b, ...updates } : b) }
        : p
    );
    updatePages(newPages);
  }, [pages, currentPageId, updatePages]);

  const handleDeleteBlock = useCallback((blockId: string) => {
    const newPages = pages.map((p) =>
      p.id === currentPageId
        ? { ...p, blocks: p.blocks.filter((b) => b.id !== blockId) }
        : p
    );
    setSelectedBlockId(null);
    updatePages(newPages);
  }, [pages, currentPageId, updatePages]);

  const handleDuplicateBlock = useCallback((blockId: string) => {
    const block = currentPage.blocks.find((b) => b.id === blockId);
    if (!block) return;
    const newBlock: LayoutBlock = {
      ...block,
      id: genId(),
      x: Math.min(12 - block.w, block.x + 1),
      y: Math.min(16 - block.h, block.y + 1),
    };
    const newPages = pages.map((p) =>
      p.id === currentPageId
        ? { ...p, blocks: [...p.blocks, newBlock] }
        : p
    );
    updatePages(newPages);
    setSelectedBlockId(newBlock.id);
  }, [pages, currentPageId, currentPage.blocks, updatePages]);

  const handleLockBlock = useCallback((blockId: string) => {
    const newPages = pages.map((p) =>
      p.id === currentPageId
        ? { ...p, blocks: p.blocks.map((b) => b.id === blockId ? { ...b, locked: !b.locked } : b) }
        : p
    );
    updatePages(newPages);
  }, [pages, currentPageId, updatePages]);

  const handleAddBlock = useCallback((type: BlockType | string, content?: Record<string, unknown>) => {
    const newBlock: LayoutBlock = {
      id: genId(),
      type: type as BlockType,
      x: 0,
      y: 0,
      w: type === 'title' ? 12 : type === 'image' ? 6 : type === 'ad' ? 4 : 4,
      h: type === 'title' ? 1 : type === 'image' ? 6 : type === 'ad' ? 4 : 5,
      content: content || {},
    };
    const newPages = pages.map((p) =>
      p.id === currentPageId
        ? { ...p, blocks: [...p.blocks, newBlock] }
        : p
    );
    updatePages(newPages);
    setSelectedBlockId(newBlock.id);
  }, [pages, currentPageId, updatePages]);

  const handleDropBlock = useCallback((type: string, x: number, y: number, extraData?: Record<string, string>) => {
    let content: Record<string, unknown> = {};

    if (type === 'article' && extraData?.articleId) {
      const article = mockArticles.find((a) => a.id === extraData.articleId);
      if (article) {
        content = {
          title: article.title,
          body: article.excerpt,
          author: article.author,
          section: article.section,
          imageUrl: article.image,
          priority: article.priority === 'alta' ? 'high' : 'medium',
          style: 'headline',
        };
      }
    } else if (type === 'image' && extraData?.imageUrl) {
      content = { imageUrl: extraData.imageUrl };
    } else if (type === 'ad' && extraData?.adId) {
      const ad = mockAds.find((a) => a.id === extraData.adId);
      if (ad) {
        content = { adName: ad.name, adClient: ad.client, imageUrl: ad.imageUrl };
      }
    }

    const newBlock: LayoutBlock = {
      id: genId(),
      type: type as BlockType,
      x,
      y,
      w: type === 'title' ? 12 : type === 'image' ? 6 : type === 'ad' ? 4 : 4,
      h: type === 'title' ? 1 : type === 'image' ? 6 : type === 'ad' ? 4 : 5,
      content,
    };

    const newPages = pages.map((p) =>
      p.id === currentPageId
        ? { ...p, blocks: [...p.blocks, newBlock] }
        : p
    );
    updatePages(newPages);
    setSelectedBlockId(newBlock.id);
  }, [pages, currentPageId, updatePages]);

  const handleApplyTemplate = useCallback((templateId: string) => {
    const templateBlocks: Record<string, LayoutBlock[]> = {
      'tpl-1': [
        { id: genId(), type: 'title', x: 0, y: 0, w: 12, h: 2, content: { title: 'JARDEL CITY POST', style: 'headline' } },
        { id: genId(), type: 'image', x: 0, y: 1, w: 8, h: 9, content: { imageUrl: 'https://public.readdy.ai/ai/img_res/9d0503246ede406f7505aefe05cb028c.jpg', caption: 'Foto de destaque' } },
        { id: genId(), type: 'article', x: 8, y: 1, w: 4, h: 5, content: { title: 'Manchete Principal', body: 'Texto da manchete principal da edição.', section: 'Política', priority: 'high', style: 'headline' } },
        { id: genId(), type: 'article', x: 8, y: 6, w: 4, h: 4, content: { title: 'Segunda Chamada', body: 'Texto da segunda chamada.', section: 'Economia', priority: 'medium', style: 'body' } },
        { id: genId(), type: 'article', x: 0, y: 10, w: 6, h: 4, content: { title: 'Terceira Chamada', body: 'Texto da terceira chamada.', section: 'Cultura', priority: 'low', style: 'body' } },
        { id: genId(), type: 'ad', x: 6, y: 10, w: 6, h: 4, content: { adName: 'Anúncio Rodapé', adClient: 'Cliente' } },
      ],
      'tpl-3': [
        { id: genId(), type: 'title', x: 0, y: 0, w: 12, h: 1, content: { title: 'SEÇÃO', style: 'headline' } },
        { id: genId(), type: 'article', x: 0, y: 1, w: 6, h: 7, content: { title: 'Artigo Principal', body: 'Texto do artigo principal desta seção.', section: 'Política', priority: 'high', style: 'headline' } },
        { id: genId(), type: 'article', x: 6, y: 1, w: 6, h: 4, content: { title: 'Artigo Secundário', body: 'Texto do artigo secundário.', section: 'Política', priority: 'medium', style: 'body' } },
        { id: genId(), type: 'article', x: 6, y: 5, w: 6, h: 3, content: { title: 'Nota Breve', body: 'Texto da nota breve.', section: 'Política', priority: 'low', style: 'caption' } },
        { id: genId(), type: 'ad', x: 0, y: 8, w: 12, h: 3, content: { adName: 'Anúncio Rodapé', adClient: 'Cliente' } },
      ],
    };

    const blocks = templateBlocks[templateId] || templateBlocks['tpl-1'];
    const newPages = pages.map((p) =>
      p.id === currentPageId ? { ...p, blocks } : p
    );
    updatePages(newPages);
    setSelectedBlockId(null);
  }, [pages, currentPageId, updatePages]);

  const handleAIApply = useCallback((action: string) => {
    if (action === 'generate-layout' || action === 'generate-front-page') {
      handleApplyTemplate('tpl-1');
    }
  }, [handleApplyTemplate]);

  const handleExportPDF = () => {
    setShowExportToast(true);
    setTimeout(() => setShowExportToast(false), 3000);
  };

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden">
      {/* Toolbar */}
      <EditorToolbar
        currentPage={currentPage.number}
        totalPages={pages.length}
        onNewArticle={() => setShowNewArticleModal(true)}
        onGenerateAI={() => setAiAction('generate-layout')}
        onPreview={() => setShowPreview(true)}
        onExportPDF={handleExportPDF}
        onAddBlock={handleAddBlock}
        zoom={zoom}
        onZoomChange={setZoom}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
      />

      {/* Main Editor Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel */}
        <EditorLeftPanel
          pages={pages}
          currentPageId={currentPageId}
          onPageSelect={setCurrentPageId}
          onReorderPages={handleReorderPages}
          onAddPage={handleAddPage}
          onDeletePage={handleDeletePage}
          onDuplicatePage={handleDuplicatePage}
          onChangePageStatus={handleChangePageStatus}
          onAddBlock={handleAddBlock}
          onApplyTemplate={handleApplyTemplate}
          onAIAction={setAiAction}
        />

        {/* Canvas */}
        <EditorCanvas
          page={currentPage}
          selectedBlockId={selectedBlockId}
          onSelectBlock={setSelectedBlockId}
          onUpdateBlock={updateBlock}
          onDropBlock={handleDropBlock}
          zoom={zoom}
        />

        {/* Right Panel */}
        <EditorRightPanel
          selectedBlock={selectedBlock}
          onUpdateBlock={handleUpdateBlockWithHistory}
          onDeleteBlock={handleDeleteBlock}
          onDuplicateBlock={handleDuplicateBlock}
          onLockBlock={handleLockBlock}
        />
      </div>

      {/* AI Modal */}
      {aiAction && (
        <AIModal
          action={aiAction}
          onClose={() => setAiAction(null)}
          onApply={handleAIApply}
        />
      )}

      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          currentPage={currentPage}
          onClose={() => setShowPreview(false)}
          onExport={handleExportPDF}
        />
      )}

      {/* New Article Modal */}
      {showNewArticleModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg">
            <div className="flex items-center gap-3 px-5 py-4 border-b-2 border-[#111111]">
              <i className="ri-file-add-line text-[#C41230] text-lg"></i>
              <p className="text-sm font-bold text-[#111111] font-serif">Novo Artigo</p>
              <button onClick={() => setShowNewArticleModal(false)} className="ml-auto text-[#999999] hover:text-[#111111] cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] font-bold text-[#999999] font-sans uppercase tracking-wide block mb-1">Título</label>
                <input type="text" className="w-full border border-[#E0E0E0] px-3 py-2 text-sm font-sans focus:outline-none focus:border-[#111111]" placeholder="Título do artigo..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-[#999999] font-sans uppercase tracking-wide block mb-1">Editoria</label>
                  <select className="w-full border border-[#E0E0E0] px-3 py-2 text-sm font-sans focus:outline-none focus:border-[#111111] bg-white cursor-pointer">
                    {['Política', 'Economia', 'Cultura', 'Esportes', 'Ciência', 'Saúde'].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#999999] font-sans uppercase tracking-wide block mb-1">Prioridade</label>
                  <select className="w-full border border-[#E0E0E0] px-3 py-2 text-sm font-sans focus:outline-none focus:border-[#111111] bg-white cursor-pointer">
                    <option value="high">Alta</option>
                    <option value="medium">Média</option>
                    <option value="low">Baixa</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold text-[#999999] font-sans uppercase tracking-wide block mb-1">Texto</label>
                <textarea rows={4} className="w-full border border-[#E0E0E0] px-3 py-2 text-sm font-sans focus:outline-none focus:border-[#111111] resize-none" placeholder="Corpo do artigo..." />
              </div>
              <div>
                <label className="text-[10px] font-bold text-[#999999] font-sans uppercase tracking-wide block mb-1">Autor</label>
                <input type="text" className="w-full border border-[#E0E0E0] px-3 py-2 text-sm font-sans focus:outline-none focus:border-[#111111]" placeholder="Nome do jornalista..." />
              </div>
            </div>
            <div className="flex gap-2 px-5 pb-5">
              <button onClick={() => setShowNewArticleModal(false)} className="flex-1 py-2 border border-[#E0E0E0] text-xs font-sans text-[#444444] hover:bg-[#F0F0F0] cursor-pointer transition-colors">
                Cancelar
              </button>
              <button
                onClick={() => {
                  handleAddBlock('article', { title: 'Novo Artigo', body: 'Texto do artigo...', section: 'Política', priority: 'medium', style: 'body' });
                  setShowNewArticleModal(false);
                }}
                className="flex-1 py-2 bg-[#111111] text-white text-xs font-sans font-bold cursor-pointer hover:bg-[#333333] transition-colors"
              >
                Adicionar ao Canvas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Added Toast */}
      {pageAddedToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#111111] text-white px-5 py-3 flex items-center gap-3 animate-fade-in">
          <div className="w-5 h-5 flex items-center justify-center bg-green-500 rounded-full">
            <i className="ri-check-line text-white text-xs"></i>
          </div>
          <div>
            <p className="text-xs font-bold font-sans">Página criada com sucesso!</p>
            <p className="text-[10px] text-white/60 font-sans">Editoria: {pageAddedToast} · Pág. {pages.length}</p>
          </div>
        </div>
      )}

      {/* Export Toast */}
      {showExportToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#111111] text-white px-4 py-3 flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <div>
            <p className="text-xs font-bold font-sans">Gerando PDF...</p>
            <p className="text-[10px] text-white/60 font-sans">Edição #14820 · {pages.length} páginas</p>
          </div>
        </div>
      )}
    </div>
  );
}
